/**
 * system-entity-adapters — część B2 mechaniki referencji (Kreator modułów).
 *
 * Gdy pole „odwołanie" w module zainstalowanym z szablonu wskazuje KARTOTEKĘ (encję
 * systemową: pracownicy / kontrahenci / użytkownicy), a nie inny moduł custom, picker
 * rekordu nie może pytać generycznego endpointu modułów custom (`/modules/:id/records`).
 * Kartoteki mają własne, niejednolite endpointy listy + szczegółu i własne kształty
 * odpowiedzi. Ten plik to JEDYNE miejsce, które te różnice opisuje — `RelationPicker`
 * w trybie kartoteki czyta stąd, jak ją odpytać i jak zbudować etykietę rekordu.
 *
 * Kontrakty potwierdzone na żywym kodzie endpointów (2026-06-08):
 *
 *   slug          lista          param   klucz listy   szczegół           klucz szczegółu  etykieta
 *   ───────────── ────────────── ─────── ───────────── ────────────────── ──────────────── ──────────────────
 *   contractors   /contractors   q       contractors   /contractors/:id   contractor       name
 *   employees     /employees     search  employees     /employees/:id     employee         firstName lastName
 *   users         /users         search  users         /users/:id         user             displayName / username (NIGDY email)
 *   products      /products      q       products       /products/:id     product          code — name
 *   companies     /companies     search  companies     /companies/:id     company          legalName / shortName
 *
 * `products` dopisany dla F.RL (powiązania między kartotekami) — picker celu powiązania
 * (`RecordLinkTargetPickerModal`) reużywa ten sam adapter dla towaru (target-only encja,
 * patrz `apps/api/app/api/record-links/route.ts` — product nie może być źródłem).
 * `companies` dopisany dla `RecordTablePicker` (picker-tabela, patrz niżej) — wspólny
 * scoping firmowy pickerów (`?companyId=`) czasem musi wybierać SAMĄ firmę.
 *
 * Żaden z endpointów listy nie wspiera pobrania „po idkach" (jak `?ids=` w modułach custom),
 * więc wybrany rekord (do wyświetlenia przy zamkniętym pickerze) dociągamy przez endpoint
 * szczegółu. Kartoteki płaskie (poza tenant-globalnymi `users`) są firmowo-zakresowe — nagłówek aktywnej firmy dokleja
 * automatycznie klient `api` (`lib/api.ts`), więc adapter nie musi się tym przejmować.
 *
 * Dodanie kolejnej kartoteki = jeden wpis w `SYSTEM_ENTITY_ADAPTERS`.
 *
 * `moduleSlug` + `coreFieldMap` (dopisane dla `RecordTablePicker` — picker-tabela,
 * modal z `ConfigurableTable` w środku zamiast dropdownu z tekstowym wyszukiwaniem):
 * `moduleSlug` wskazuje katalog pól (`GET /system-modules/{moduleSlug}/fields`) do
 * zbudowania kolumn core+ext; `coreFieldMap` mapuje core-slug -> klucz camelCase
 * w odpowiedzi listy z projekcją `?columns=`. Wszystkie 5 kartotek płaskich
 * (`contractors`/`employees`/`users`/`products`/`companies`) mają dziś ten kontrakt
 * po stronie backendu (`CORE_COLUMN_WHITELIST` w odpowiadającym `apps/api/app/api/
 * <encja>/route.ts`) i `coreFieldMap` będący jego 1:1 lustrem — `RecordTablePicker`
 * wspiera więc kolumny dla każdej z nich. `documents` wspiera kolumny inną ścieżką
 * (`classDimension.resolveForClass`, patrz niżej), nie płaskim `coreFieldMap`.
 *
 * `documents` (F20 — dokumenty jako cel odwołania, `GET /api/documents`) jest
 * SPECJALNYM adapterem: jeden wpis kryje SIEDEM klas dokumentów (`purchase_invoices`,
 * `sale_invoices`, `contracts`, `letters`, `commercial`, `internal`, `specjalne`), wybieranych w
 * RUNTIME wewnątrz pickera (`classDimension`), nie w konfiguracji pola. Różni się od
 * pozostałych pięciu (płaskich) adapterów w dwóch miejscach:
 *  - brak endpointu „szczegół po id" (`detailPath`/`detailResponseKey`) — zamiast tego
 *    `resolveByIds` (batch `?ids=`, patrz tryb 1 w `apps/api/app/api/documents/route.ts`);
 *  - domyślny widok (bez wybranej klasy) NIE ma katalogu pól (`/system-modules/documents/
 *    fields` nie istnieje) — kolumny są STAŁE (`DOCUMENT_SHARED_COLUMNS`), nie budowane
 *    z katalogu. Wybór klasy w pickerze przełącza na zwykłą ścieżkę katalog+`?columns=`
 *    (`classDimension.resolveForClass`), identyczną co do kształtu z `contractors`.
 */

import type { DocumentClass } from '@saas/shared';

export interface SystemEntityAdapter {
  /** Slug kartoteki (= wartość `relationSystemModuleSlug` na polu). */
  slug: string;
  /** Ścieżka endpointu listy (przekazywana do `api.get`). */
  listPath: string;
  /** Nazwa parametru wyszukiwania w query stringu (`q` lub `search`). */
  searchParam: string;
  /** Klucz tablicy rekordów w odpowiedzi listy. */
  listResponseKey: string;
  /**
   * Buduje ścieżkę endpointu szczegółu dla danego id. `undefined` = encja NIE ma
   * endpointu „szczegół po id" (dziś: WYŁĄCZNIE `documents` — patrz `resolveByIds`
   * niżej, batch `?ids=` zamiast pojedynczego GET per id). Konsumenci (`RecordTablePicker`
   * prefetch, `ExtFieldValueInput` etykieta, `RelationCellLabel`) sprawdzają
   * `resolveByIds` NAJPIERW i używają `detailPath`/`detailResponseKey` tylko gdy go brak.
   */
  detailPath?: (id: string) => string;
  /** Klucz obiektu rekordu w odpowiedzi szczegółu (mirror `detailPath` — patrz wyżej). */
  detailResponseKey?: string;
  /** Z surowego rekordu (z listy, szczegółu lub `resolveByIds`) składa etykietę do wyświetlenia. */
  getLabel: (raw: Record<string, unknown>) => string;
  /**
   * Kolumny okna wyboru (`RecordTablePicker`) przypięte na początek, w tej kolejności — to,
   * po czym user rozpoznaje rekord (np. nazwa + NIP kontrahenta), ma być widoczne bez
   * przewijania w bok. Reszta kolumn idzie za nimi w kolejności katalogu pól.
   */
  pickerLeadingColumns?: readonly string[];
  /**
   * Slug modułu systemowego dla katalogu pól (`GET /system-modules/{moduleSlug}/fields`)
   * — dodane dla `RecordTablePicker` (picker-tabela, B2 mechaniki referencji, część 2).
   * Dziś zawsze równe `slug` (kartoteki systemowe rejestrują moduł pod tym samym
   * sluglem), trzymane jako osobne pole żeby adapter mógł kiedyś zdefiniować własny
   * `moduleSlug` bez zakładania relacji 1:1 ze slugiem kartoteki. Dla `documents`
   * (adapter z `classDimension`) ta wartość jest bez znaczenia — moduleSlug per-klasy
   * pochodzi z `classDimension.resolveForClass`.
   */
  moduleSlug: string;
  /**
   * Mapa core-slug (snake_case, jak w `system_module_fields.slug`) -> klucz camelCase
   * w odpowiedzi listy encji z projekcją `?columns=` (np. kontrahenci: `short_name` ->
   * `shortName`). Wymagane przez `RecordTablePicker` do zbudowania `ColumnDefinition[]`
   * z katalogu pól — bez tej mapy picker nie wie, pod jakim kluczem odczytać dane
   * core-pole z projektowanego wiersza.
   *
   * `undefined` = encja NIE wspiera jeszcze kontraktu projekcji `?columns=` po stronie
   * backendu — `RecordTablePicker` pokazuje wtedy komunikat braku wsparcia zamiast
   * próbować zbudować puste/błędne kolumny. Dziś WSZYSTKIE 5 kartotek płaskich mają tę
   * mapę zdefiniowaną (backend wspiera `?columns=` dla każdej). Dla `documents` ta
   * wartość jest bez znaczenia — patrz `classDimension` niżej.
   */
  coreFieldMap?: Record<string, string>;
  /**
   * Czy picker rekordu ma zawężać listę do AKTYWNEJ firmy (`?companyId=` na endpoincie
   * listy). Domyślnie `true` (brak pola = `true`) — kartoteki firmowo-zakresowe
   * (kontrahenci, pracownicy, towary, dokumenty) mają wtedy ten sam zakres, co reszta
   * ekranu. `false` dla `companies`: tam `?companyId=` zawęża listę do JEDNEJ firmy
   * (właśnie tej aktywnej), więc wybór JAKIEJKOLWIEK innej firmy byłby niemożliwy —
   * picker firm musi widzieć wszystkie firmy usera. Czytane przez konsumentów pickera
   * (`AdditionalReferenceCell`), nie przez sam `RecordTablePicker`.
   */
  scopeByActiveCompany?: boolean;
  /**
   * Batch resolve-by-id — dziś WYŁĄCZNIE `documents` (jedyna encja z endpointem
   * `?ids=<uuid,uuid,…>`, tryb 1 `GET /api/documents`). Gdy obecne, konsumenci
   * (`RecordTablePicker` prefetch wielokrotnego wyboru, `ExtFieldValueInput` etykieta
   * wybranej wartości, `RelationCellLabel`) wołają TO zamiast pętli
   * `detailPath`/`detailResponseKey` per-id. `undefined` = zachowanie dotychczasowe
   * (pięć kartotek płaskich, endpoint „szczegół" per rekord).
   */
  resolveByIds?: {
    /** Buduje ścieżkę endpointu batch-resolve dla podanej listy id (np. `/documents?ids=a,b`). */
    path: (ids: string[]) => string;
    /** Klucz tablicy rekordów w odpowiedzi (np. `documents`). */
    responseKey: string;
  };
  /**
   * Wymiar KLASY — dziś WYŁĄCZNIE `documents` (F20: token celu odwołania jest JEDEN
   * generyczny `'documents'`, klasa dokumentu jest filtrem wybieranym w RUNTIME
   * wewnątrz `RecordTablePicker`, patrz nagłówek pliku). Obecność tego pola przełącza
   * picker w tryb „selektor klasy": domyślnie (brak wybranej klasy) picker używa pól
   * płaskich adaptera (`listPath`/`coreFieldMap` — dla `documents` to STAŁY zestaw
   * kolumn wspólnych, bez katalogu pól); po wybraniu klasy picker woła
   * `resolveForClass(classSlug)` i przełącza się na katalog+`?columns=`, identycznie
   * jak w trybie kartoteki płaskiej. `undefined` = zachowanie dotychczasowe (pięć
   * kartotek płaskich, picker nie renderuje selektora klasy).
   */
  classDimension?: DocumentClassDimension;
}

/** Jedna z 7 klas dokumentów oferowanych w selektorze klasy pickera (`documents`). */
export interface DocumentClassOption {
  /**
   * Slug kartoteki klasy — WARTOŚĆ `?class=` na `GET /api/documents` ORAZ `moduleSlug`
   * katalogu pól tej klasy (`GET /system-modules/{slug}/fields`); te dwa są tym samym
   * identyfikatorem (mirror `CLASS_KARTOTEKA_SLUGS` w backendzie,
   * `apps/api/lib/documents/class-kartoteka-slug.ts`). UWAGA: dla faktur/umów/pism
   * różni się od wartości `documents.class` w bazie (`DocumentClass`, liczba pojedyncza:
   * `purchase_invoice`/`contract`/`letter`) — patrz `DOCUMENT_CLASS_ENUM_TO_SLUG` niżej.
   */
  slug: string;
  /** i18n key etykiety opcji/zakładki klasy w selektorze. */
  labelKey: string;
}

/** Rozwiązanie adaptera per wybrana klasa dokumentu (`RecordTablePicker`, tryb „klasa wybrana"). */
export interface DocumentClassResolution {
  /** Ścieżka endpointu listy — dziś zawsze `/documents` (jedno `?class=` różnicuje klasy). */
  listPath: string;
  /** Slug modułu systemowego klasy — katalog pól `GET /system-modules/{moduleSlug}/fields`. */
  moduleSlug: string;
  /** Mapa core-slug -> klucz camelCase odpowiedzi, PER GRUPA (fakturowa/standardowa — patrz plik). */
  coreFieldMap: Record<string, string>;
}

export interface DocumentClassDimension {
  /** 7 klas, w kolejności wyświetlania w selektorze. */
  classes: DocumentClassOption[];
  /** Rozwiązuje listPath/moduleSlug/coreFieldMap DLA WYBRANEJ klasy (`slug` z `classes`). */
  resolveForClass: (classSlug: string) => DocumentClassResolution;
}

/** Kolumna STAŁA (bez katalogu pól) — domyślny widok `documents` (wszystkie klasy naraz). */
export interface DocumentSharedColumnDef {
  /** Klucz w odpowiedzi WSPÓLNEGO kształtu (`SHARED_SELECT_SHAPE`, `GET /api/documents` tryb 3). */
  key: string;
  /** i18n key nagłówka kolumny — `RecordTablePicker` rozwiązuje przez `t()` (brak katalogu = brak `name` gotowego z backendu). */
  labelKey: string;
  /**
   * Nazwa pola dla `?headerFilters=` w trybie 3 (`GET /api/documents` bez `?class=`) —
   * `SHARED_CORE_COLUMN_MAP` w `apps/api/app/api/documents/route.ts` filtruje po snake_case,
   * podczas gdy `key` tutaj jest camelCase (klucz odpowiedzi `SHARED_SELECT_SHAPE`).
   * `RecordTablePicker` tłumaczy `key` -> `filterField` przy budowie `?headerFilters=`
   * (mirror `column.headerFilter?.fieldSlug` w `apps/admin/src/pages/Contractors.tsx`).
   * `undefined` = kolumna niefiltrowalna (dziś WYŁĄCZNIE `class` — kolumna LICZONA z
   * `row.class`, nie realna kolumna SQL; backend i tak cicho ignoruje filtr na tym polu —
   * dyskryminator klasy jest już egzekwowany przez `resolveActiveDocumentClasses`).
   */
  filterField?: string;
}

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

/** Fallback gdy żadne pole etykiety nie jest dostępne (np. zamaskowane PII). */
function idFallback(raw: Record<string, unknown>): string {
  const id = str(raw.id);
  return id ? id.slice(0, 8) + '…' : '—';
}

// ============================================================================
// `documents` — wymiar klasy (F20). Patrz nagłówek pliku + komentarze na
// `classDimension` w `SystemEntityAdapter` dla kontekstu ogólnego.
// ============================================================================

/**
 * 7 klas dokumentów, w kolejności wyświetlania selektora — slug = wartość `?class=`
 * na `GET /api/documents` ORAZ `moduleSlug` katalogu pól tej klasy (mirror
 * `CLASS_KARTOTEKA_SLUGS` w `apps/api/lib/documents/class-kartoteka-slug.ts`).
 */
export const DOCUMENT_CLASS_OPTIONS: DocumentClassOption[] = [
  {
    slug: 'purchase_invoices',
    labelKey: 'ui.recordTablePicker.documentClasses.purchase_invoices',
  },
  { slug: 'sale_invoices', labelKey: 'ui.recordTablePicker.documentClasses.sale_invoices' },
  { slug: 'contracts', labelKey: 'ui.recordTablePicker.documentClasses.contracts' },
  { slug: 'letters', labelKey: 'ui.recordTablePicker.documentClasses.letters' },
  { slug: 'commercial', labelKey: 'ui.recordTablePicker.documentClasses.commercial' },
  { slug: 'internal', labelKey: 'ui.recordTablePicker.documentClasses.internal' },
  { slug: 'specjalne', labelKey: 'ui.recordTablePicker.documentClasses.specjalne' },
];

/**
 * `documents.class` w bazie (`DocumentClass` z `@saas/shared`, liczba pojedyncza) ->
 * slug kartoteki (liczba mnoga dla faktur/umów/pism — patrz `DocumentClassOption.slug`
 * powyżej). Używane WYŁĄCZNIE do renderowania kolumny „Klasa" w domyślnym widoku
 * pickera (wszystkie klasy naraz, `row.class` niesie tę WARTOŚĆ pojedynczą) — pozwala
 * zmapować ją na TEN SAM klucz i18n co selektor klasy, bez duplikowania tłumaczeń.
 */
const DOCUMENT_CLASS_ENUM_TO_SLUG: Record<DocumentClass, string> = {
  purchase_invoice: 'purchase_invoices',
  sale_invoice: 'sale_invoices',
  contract: 'contracts',
  letter: 'letters',
  commercial: 'commercial',
  internal: 'internal',
  specjalne: 'specjalne',
};

/** i18n key etykiety klasy dla wartość `row.class` (liczba pojedyncza) — `null` gdy nierozpoznana. */
export function documentClassLabelKey(rowClassValue: unknown): string | null {
  if (typeof rowClassValue !== 'string') return null;
  const slug = DOCUMENT_CLASS_ENUM_TO_SLUG[rowClassValue as DocumentClass];
  if (!slug) return null;
  return DOCUMENT_CLASS_OPTIONS.find((c) => c.slug === slug)?.labelKey ?? null;
}

/**
 * Grupa STANDARDOWA (contracts/letters/commercial/internal/specjalne) — 14 pól core, identyczne dla 5 klas.
 * MUSI POKRYWAĆ (podzbiór) `STANDARD_CORE_COLUMN_WHITELIST` w `apps/api/app/api/documents/
 * route.ts` dla slugów faktycznie renderowanych jako kolumny pickera — NIE jest gwarantowanym
 * 1:1 lustrem CAŁEJ whitelisty backendu (ta może kiedyś urosnąć o pola bez odpowiadającego
 * wpisu w katalogu `system_module_fields`, których picker i tak nigdy nie zobaczy — patrz
 * `visibleFields` w `RecordTablePicker.tsx`, filtr `f.slug in coreFieldMap`).
 */
const STANDARD_CORE_FIELD_MAP: Record<string, string> = {
  system_number: 'systemNumber',
  title: 'title',
  status: 'status',
  document_date: 'documentDate',
  description: 'description',
  urgency: 'urgency',
  owner_user_id: 'ownerUserId',
  company_id: 'companyId',
  notes: 'notes',
  rejection_reason: 'rejectionReason',
  source: 'source',
  external_id: 'externalId',
  external_source: 'externalSource',
};

/**
 * Grupa FAKTUROWA (purchase_invoices/sale_invoices) — 19 pól documents.* + 11 pól detali
 * faktury. MUSI POKRYWAĆ (podzbiór) `INVOICE_CORE_COLUMN_WHITELIST` w `apps/api/app/api/
 * documents/route.ts` dla slugów faktycznie renderowanych jako kolumny pickera — NIE jest
 * gwarantowanym 1:1 lustrem CAŁEJ whitelisty backendu (patrz komentarz przy
 * `STANDARD_CORE_FIELD_MAP` wyżej — ten sam zastrzeżenie).
 */
const INVOICE_CORE_FIELD_MAP: Record<string, string> = {
  system_number: 'systemNumber',
  status: 'status',
  urgency: 'urgency',
  owner_user_id: 'ownerUserId',
  company_id: 'companyId',
  issue_date: 'documentDate',
  received_date: 'receivedDate',
  vendor_id: 'contractorId',
  vendor_tax_id_cached: 'counterpartyTaxIdCached',
  vendor_name_cached: 'counterpartyNameCached',
  currency_code: 'currencyCode',
  exchange_rate: 'exchangeRate',
  amount_net: 'amountNet',
  amount_vat: 'amountVat',
  amount_gross: 'amountGross',
  description: 'description',
  rejection_reason: 'rejectionReason',
  notes: 'notes',
  document_type: 'invoiceKind',
  invoice_number: 'invoiceNumber',
  ksef_number: 'ksefNumber',
  // Ewidencja KSeF/JPK (0159) — konwersja sluga na klucz odpowiedzi wypada tu mechanicznie
  // tak samo, ale mapa jest deklarowanym lustrem whitelisty backendu; trzymamy ją kompletną.
  purchase_evidence_marker: 'purchaseEvidenceMarker',
  jpk_document_marker: 'jpkDocumentMarker',
  corrected_invoice_id: 'correctedInvoiceId',
  sale_date: 'saleDate',
  payment_due_date: 'paymentDueDate',
  payment_method: 'paymentMethod',
  paid_at: 'paidAt',
  vendor_bank_account_id: 'vendorBankAccountId',
  payment_notes: 'paymentNotes',
  cost_center_default: 'costCenterDefault',
};

const INVOICE_CLASS_SLUGS: ReadonlySet<string> = new Set(['purchase_invoices', 'sale_invoices']);

/**
 * Kolumny STAŁE domyślnego widoku `documents` (bez wybranej klasy — wszystkie klasy
 * naraz, `GET /api/documents` tryb 3). Podzbiór `SHARED_SELECT_SHAPE` z backendu:
 * ŚWIADOMIE bez `id`/`companyId`/`contractorId`/`ownerUserId` — trzy ostatnie to surowe
 * FK UUID bez lokalnego adaptera do rozwiązania etykiety w tym przekrojowym (wielo-
 * klasowym) kontekście; pokazanie gołego UUID nie pomaga userowi zidentyfikować
 * rekordu, więc pominięte z widoku (dane i tak trafiają w wierszu, gdyby ktoś kiedyś
 * chciał je dodać — to tylko lista kolumn WIDOCZNYCH).
 */
export const DOCUMENT_SHARED_COLUMNS: DocumentSharedColumnDef[] = [
  // `class` bez `filterField` — kolumna LICZONA (etykieta z `row.class`), nie realna kolumna
  // SQL filtrowalna przez `SHARED_CORE_COLUMN_MAP` (patrz `DocumentSharedColumnDef.filterField`).
  { key: 'class', labelKey: 'ui.recordTablePicker.documentColumns.class' },
  {
    key: 'systemNumber',
    labelKey: 'ui.recordTablePicker.documentColumns.systemNumber',
    filterField: 'system_number',
  },
  { key: 'title', labelKey: 'ui.recordTablePicker.documentColumns.title', filterField: 'title' },
  {
    key: 'status',
    labelKey: 'ui.recordTablePicker.documentColumns.status',
    filterField: 'status',
  },
  {
    key: 'urgency',
    labelKey: 'ui.recordTablePicker.documentColumns.urgency',
    filterField: 'urgency',
  },
  {
    key: 'documentDate',
    labelKey: 'ui.recordTablePicker.documentColumns.documentDate',
    filterField: 'document_date',
  },
  {
    key: 'receivedDate',
    labelKey: 'ui.recordTablePicker.documentColumns.receivedDate',
    filterField: 'received_date',
  },
  {
    key: 'counterpartyNameCached',
    labelKey: 'ui.recordTablePicker.documentColumns.counterpartyNameCached',
    filterField: 'counterparty_name_cached',
  },
  {
    key: 'counterpartyTaxIdCached',
    labelKey: 'ui.recordTablePicker.documentColumns.counterpartyTaxIdCached',
    filterField: 'counterparty_tax_id_cached',
  },
  {
    key: 'currencyCode',
    labelKey: 'ui.recordTablePicker.documentColumns.currencyCode',
    filterField: 'currency_code',
  },
  {
    key: 'amountNet',
    labelKey: 'ui.recordTablePicker.documentColumns.amountNet',
    filterField: 'amount_net',
  },
  {
    key: 'amountVat',
    labelKey: 'ui.recordTablePicker.documentColumns.amountVat',
    filterField: 'amount_vat',
  },
  {
    key: 'amountGross',
    labelKey: 'ui.recordTablePicker.documentColumns.amountGross',
    filterField: 'amount_gross',
  },
  {
    key: 'description',
    labelKey: 'ui.recordTablePicker.documentColumns.description',
    filterField: 'description',
  },
  { key: 'notes', labelKey: 'ui.recordTablePicker.documentColumns.notes', filterField: 'notes' },
];

export const SYSTEM_ENTITY_ADAPTERS: Record<string, SystemEntityAdapter> = {
  contractors: {
    slug: 'contractors',
    listPath: '/contractors',
    searchParam: 'q',
    listResponseKey: 'contractors',
    detailPath: (id) => `/contractors/${id}`,
    detailResponseKey: 'contractor',
    getLabel: (raw) => str(raw.name) || idFallback(raw),
    moduleSlug: 'contractors',
    pickerLeadingColumns: ['name', 'short_name', 'tax_id'],
    // Whitelist core-slugów `GET /api/contractors` z projekcją `?columns=` — patrz
    // `CORE_COLUMN_WHITELIST` w `apps/api/app/api/contractors/route.ts` (jedno
    // źródło prawdy po stronie backendu; ta mapa jest jego lustrem po stronie UI).
    coreFieldMap: {
      name: 'name',
      short_name: 'shortName',
      tax_id: 'taxId',
      status: 'status',
      object_type: 'objectType',
      phone: 'phone',
      email: 'email',
    },
  },
  employees: {
    slug: 'employees',
    listPath: '/employees',
    searchParam: 'search',
    listResponseKey: 'employees',
    detailPath: (id) => `/employees/${id}`,
    detailResponseKey: 'employee',
    getLabel: (raw) => {
      const full = `${str(raw.firstName)} ${str(raw.lastName)}`.trim();
      return full || str(raw.workEmail) || idFallback(raw);
    },
    moduleSlug: 'employees',
    // Whitelist core-slugów `GET /api/employees` z projekcją `?columns=` — patrz
    // `CORE_COLUMN_WHITELIST` w `apps/api/app/api/employees/route.ts` (1:1 lustro).
    coreFieldMap: {
      last_name: 'lastName',
      first_name: 'firstName',
      middle_name: 'middleName',
      company_id: 'companyId',
      // F24 Pakiet 0: `position` (wolny tekst) → `position_code` (kod słownika
      // `employee_positions`); `department_id` → `org_unit_id` (jednostka organizacyjna).
      position_code: 'positionCode',
      org_unit_id: 'orgUnitId',
      employment_type: 'employmentType',
      has_account: 'hasAccount',
      is_active: 'isActive',
      gcrecord: 'gcrecord',
      work_email: 'workEmail',
    },
  },
  users: {
    slug: 'users',
    listPath: '/users',
    searchParam: 'search',
    listResponseKey: 'users',
    detailPath: (id) => `/users/${id}`,
    detailResponseKey: 'user',
    // displayName-first — lustro backendu (lista `users` etykietuje `displayName ||
    // username`, email celowo pominięty tam) — displayName-first PO OBU STRONACH,
    // fallback też na `username` (nie `email`), żeby optymistyczna etykieta pickera
    // (przed refetchem z backendu) nigdy nie pokazała adresu e-mail dla usera bez
    // displayName. UWAGA: picker (`RecordTablePicker`) czyta wiersz z projekcji
    // `?columns=` zbudowanej z katalogu pól po fladze `showInList` (nie z pełnej
    // odpowiedzi `GET /api/users`) — `username` jest w niej obecny tylko gdy pole
    // „Login" ma `showInList: true` w katalogu modułu `users` (tak jest domyślnie
    // w seedzie). Gdy tenant świadomie ukryje tę kolumnę z listy, optymistyczna
    // etykieta spada od razu na skrócone id — NIGDY na e-mail, który tu jest
    // świadomie niedopuszczalnym fallbackiem, nie brakującym.
    getLabel: (raw) => str(raw.displayName) || str(raw.username) || idFallback(raw),
    moduleSlug: 'users',
    pickerLeadingColumns: ['display_name', 'email', 'username'],
    // Whitelist core-slugów `GET /api/users` z projekcją `?columns=` — patrz
    // `CORE_COLUMN_WHITELIST` w `apps/api/app/api/users/route.ts` (1:1 lustro).
    coreFieldMap: {
      email: 'email',
      username: 'username',
      display_name: 'displayName',
      language: 'language',
      user_type: 'userType',
      is_super_user: 'isSuperUser',
      default_company_id: 'defaultCompanyId',
      status: 'status',
      last_login_at: 'lastLoginAt',
      invited_at: 'invitedAt',
      has_account: 'hasAccount',
      gcrecord: 'gcrecord',
    },
  },
  products: {
    slug: 'products',
    listPath: '/products',
    searchParam: 'q',
    listResponseKey: 'products',
    detailPath: (id) => `/products/${id}`,
    detailResponseKey: 'product',
    // Lustro backendu: `code — name` gdy oba obecne; sam `name` bez kodu; sam `code`
    // gdy nazwa pusta (backend zwraca kod, nie id-fallback) — id-fallback WYŁĄCZNIE
    // gdy brak obu.
    getLabel: (raw) => {
      const name = str(raw.name);
      const code = str(raw.code);
      if (name && code) return `${code} — ${name}`;
      if (name) return name;
      if (code) return code;
      return idFallback(raw);
    },
    moduleSlug: 'products',
    // Whitelist core-slugów `GET /api/products` z projekcją `?columns=` — patrz
    // `PRODUCT_CORE_COLUMN_WHITELIST` w `apps/api/lib/products/product-list-descriptor.ts`
    // (1:1 lustro; ta trasa woła `/products?columns=` BEZ `?withStock=`, więc idzie przez
    // deskryptor silnika list, nie przez `CORE_COLUMN_WHITELIST` w `route.ts` — ta ostatnia
    // po migracji obsługuje już wyłącznie gałąź `?withStock=true`).
    coreFieldMap: {
      code: 'code',
      name: 'name',
      product_type: 'productType',
      unit: 'unit',
      category: 'category',
      vat_rate: 'vatRate',
      base_price: 'basePrice',
      is_active: 'isActive',
      source: 'source',
      currency_code: 'currencyCode',
    },
  },
  companies: {
    slug: 'companies',
    listPath: '/companies',
    // `GET /api/companies` na silniku list czyta wyłącznie `?q=` (nie dawny `?search=`) — picker
    // relacji „Firmy" (RelationPicker) buduje URL z `searchParam`, więc bez tego type-ahead
    // cicho gubiłby frazę (200, pierwsze N firm bez filtra). Mirror `documents`/`products` (`q`).
    searchParam: 'q',
    listResponseKey: 'companies',
    detailPath: (id) => `/companies/${id}`,
    detailResponseKey: 'company',
    getLabel: (raw) => str(raw.legalName) || str(raw.shortName) || idFallback(raw),
    moduleSlug: 'companies',
    // Wyjątek od domyślnego zawężania (patrz `scopeByActiveCompany` w interfejsie):
    // `GET /api/companies?companyId=` zwraca DOKŁADNIE jedną firmę, więc zawężenie
    // do aktywnej firmy zostawiłoby w pickerze jedyną opcję = ta, którą user już ma.
    scopeByActiveCompany: false,
    // Whitelist core-slugów `GET /api/companies` z projekcją `?columns=` — patrz
    // `CORE_COLUMN_WHITELIST` w `apps/api/app/api/companies/route.ts` (1:1 lustro).
    coreFieldMap: {
      legal_name: 'legalName',
      short_name: 'shortName',
      tax_id: 'taxId',
      email: 'email',
      phone: 'phone',
      website: 'website',
      is_primary: 'isPrimary',
      is_active: 'isActive',
    },
  },
  // `documents` (F20) — JEDEN generyczny token celu odwołania dla dowolnej z 7 klas
  // dokumentów; klasa jest filtrem wybieranym w runtime wewnątrz `RecordTablePicker`
  // (`classDimension`), nie częścią targetu pola. Patrz nagłówek pliku.
  documents: {
    slug: 'documents',
    listPath: '/documents',
    searchParam: 'q',
    listResponseKey: 'documents',
    // Brak detailPath/detailResponseKey — `GET /api/documents` nie ma endpointu
    // „szczegół po id" (patrz `resolveByIds` niżej, batch `?ids=`).
    getLabel: (raw) => {
      const systemNumber = str(raw.systemNumber);
      const title = str(raw.title);
      if (systemNumber && title) return `${systemNumber} — ${title}`;
      return systemNumber || title || idFallback(raw);
    },
    // moduleSlug płaski bez znaczenia (documents ma classDimension) — token spójny z resztą.
    moduleSlug: 'documents',
    resolveByIds: {
      path: (ids) => `/documents?ids=${ids.map(encodeURIComponent).join(',')}`,
      responseKey: 'documents',
    },
    classDimension: {
      classes: DOCUMENT_CLASS_OPTIONS,
      resolveForClass: (classSlug) => ({
        listPath: '/documents',
        moduleSlug: classSlug,
        coreFieldMap: INVOICE_CLASS_SLUGS.has(classSlug)
          ? INVOICE_CORE_FIELD_MAP
          : STANDARD_CORE_FIELD_MAP,
      }),
    },
  },
};

/** Zwraca adapter kartoteki dla slugu albo `undefined`, gdy slug nieobsługiwany w runtime. */
export function getSystemEntityAdapter(
  slug: string | null | undefined,
): SystemEntityAdapter | undefined {
  if (!slug) return undefined;
  return SYSTEM_ENTITY_ADAPTERS[slug];
}

/**
 * Lista celów odwołania do wyboru w kreatorze pól tabeli dodatkowej (F.REF, tabele dodatkowe) —
 * mirror `KNOWN_REFERENCE_TARGET_SLUGS` (`apps/api/lib/kartoteki/known-slugs.ts`), kolejność =
 * kolejność w select-cie. `AddSystemFieldModal.tsx` (zwykłe pola kartoteki) ma dziś WŁASNĄ,
 * niezależną kopię tych samych 6 opcji z osobnymi kluczami i18n
 * (`tenant.systemModuleFields.modal.referenceTarget*`) — migracja tamtego modala na tę wspólną
 * listę to świadomie osobny, opcjonalny follow-up, nie część tego zadania.
 */
export const REFERENCE_TARGET_OPTIONS = [
  { value: 'contractors', labelKey: 'common.referenceTarget.contractors' },
  { value: 'employees', labelKey: 'common.referenceTarget.employees' },
  { value: 'users', labelKey: 'common.referenceTarget.users' },
  { value: 'products', labelKey: 'common.referenceTarget.products' },
  { value: 'companies', labelKey: 'common.referenceTarget.companies' },
  { value: 'documents', labelKey: 'common.referenceTarget.documents' },
] as const satisfies ReadonlyArray<{ value: string; labelKey: string }>;

/**
 * Unia dozwolonych celów odwołania — wyprowadzona z `REFERENCE_TARGET_OPTIONS` (jedno
 * źródło prawdy), żeby literówka w kodzie UI padała na `tsc`, a nie dopiero na
 * `INVALID_RELATION_TARGET` z backendu.
 */
export type ReferenceTargetSlug = (typeof REFERENCE_TARGET_OPTIONS)[number]['value'];

const REFERENCE_TARGET_SLUG_SET: ReadonlySet<string> = new Set(
  REFERENCE_TARGET_OPTIONS.map((opt) => opt.value),
);

/**
 * Zawęża surowy string (typowo `event.target.value` selecta albo wartość pola z API)
 * do znanego celu odwołania. `null` = wartość spoza listy (brak celu / cel nieznany
 * temu buildowi UI) — wołający decyduje, czy to blokada zapisu, czy stan „legacy".
 */
export function toReferenceTargetSlug(
  value: string | null | undefined,
): ReferenceTargetSlug | null {
  return value && REFERENCE_TARGET_SLUG_SET.has(value) ? (value as ReferenceTargetSlug) : null;
}

/** Domyślny target oferowany przy przełączeniu typu pola na `reference` (pierwsza kartoteka płaska). */
export const DEFAULT_REFERENCE_TARGET: ReferenceTargetSlug = 'contractors';
